import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const schema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(80),
});

export type UnitFormValues = z.infer<typeof schema>;

interface UnitFormDialogProps {
  title: string;
  initial_values?: Partial<UnitFormValues>;
  submitting: boolean;
  onSubmit: (values: UnitFormValues) => Promise<void>;
  onCancel: () => void;
}

export function UnitFormDialog({
  title,
  initial_values,
  submitting,
  onSubmit,
  onCancel,
}: UnitFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<UnitFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: initial_values?.code ?? '',
      name: initial_values?.name ?? '',
    },
  });

  const onSubmitForm = async (values: UnitFormValues): Promise<void> => {
    await onSubmit(values);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <form onSubmit={form.handleSubmit(onSubmitForm)} noValidate>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('catalog.units.code')}</Label>
              <Input id="code" autoFocus maxLength={20} {...form.register('code')} />
              {form.formState.errors.code ? (
                <p className="text-sm text-destructive">{form.formState.errors.code.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('catalog.units.name')}</Label>
              <Input id="name" maxLength={80} {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
